import { useNavigate } from "react-router-dom";
import { thumb } from "../lib/img";
import { sizeLabel } from "../lib/sizes";
import { ShoppingBag, Trash2, Minus, Plus, ArrowRight, Truck, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import { loadDeliveryInfo } from "../lib/checkoutStorage";

export default function CartPage() {
  const { cart, loading, updateCartItem, removeFromCart, clearCart } = useCart();
  const navigate = useNavigate();

  const deliveryInfo = loadDeliveryInfo();
  const deliveryCost = deliveryInfo?.delivery_cost || null;

  const handleClearCart = async () => {
    await clearCart();
    toast.success("Carrello svuotato");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen py-16 px-6" data-testid="cart-empty">
        <div className="max-w-2xl mx-auto text-center">
          <ShoppingBag className="h-16 w-16 text-sage-light mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-forest mb-4">Il tuo carrello e vuoto</h1>
          <p className="text-sage mb-8">Cerca un indirizzo per trovare il fiorista e i prodotti giusti per te.</p>
          <Button
            onClick={() => navigate("/shops")}
            className="bg-forest text-white hover:bg-forest-light"
            data-testid="cart-browse-shops-button"
          >
            Invia Fiori
          </Button>
        </div>
      </div>
    );
  }

  const subtotal = cart.total;
  const total = deliveryCost ? subtotal + deliveryCost : subtotal;

  return (
    <div className="min-h-screen py-12 px-6" data-testid="cart-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-serif text-forest">Il tuo Carrello</h1>
          <Button
            variant="ghost"
            onClick={handleClearCart}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 text-sm"
            data-testid="clear-cart-btn"
          >
            <X className="h-4 w-4 mr-1" /> Svuota carrello
          </Button>
        </div>

        {/* Delivery address reminder */}
        {deliveryInfo && (
          <div className="bg-brand/5 border border-brand/20 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="cart-delivery-banner">
            <Truck className="h-5 w-5 text-brand shrink-0" />
            <div className="flex-1 text-sm">
              <p className="text-forest">
                Consegna a <strong>{deliveryInfo.address}{deliveryInfo.cap ? `, ${deliveryInfo.cap}` : ""}, {deliveryInfo.city}</strong>
              </p>
              {deliveryCost !== null && (
                <p className="text-brand font-semibold">Spese di consegna: {"\u20AC"}{deliveryCost.toFixed(2)}</p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <div
                key={`${item.product_id}-${item.size || "standard"}`}
                className="flex gap-4 bg-white p-4 border border-cream-dark rounded-lg"
                data-testid={`cart-item-${item.product_id}`}
              >
                <div className="shrink-0 w-24 h-24 bg-cream rounded flex items-center justify-center">
                  {item.product.images?.[0] ? (
                    <img src={thumb(item.product.images[0], 320)} alt={item.product.name} loading="lazy" decoding="async" className="w-24 h-24 object-cover rounded" />
                  ) : (
                    <ShoppingBag className="h-8 w-8 text-sage-light" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <div>
                      <p className="font-serif text-forest">{item.product.name}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-sage">{item.product.shop_name}</p>
                        {item.size && (
                          <span className="text-xs bg-brand/10 text-brand px-2 py-0.5 rounded-full">{sizeLabel(item.size)}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.product_id, item.size || "standard")}
                      className="text-sage-light hover:text-red-500 transition-colors"
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-cream-dark rounded">
                      <button
                        onClick={() => updateCartItem(item.product_id, item.quantity - 1, item.size || "standard")}
                        className="p-2 hover:bg-cream transition-colors"
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="px-4 font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateCartItem(item.product_id, item.quantity + 1, item.size || "standard")}
                        className="p-2 hover:bg-cream transition-colors"
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="font-semibold text-forest">{"\u20AC"}{item.item_total.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 border border-cream-dark rounded-lg sticky top-24">
              <h2 className="font-serif text-xl text-forest mb-4">Riepilogo</h2>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sage">
                  <span>Subtotale</span>
                  <span>{"\u20AC"}{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sage">
                  <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" />Consegna</span>
                  <span>{deliveryCost !== null ? `\u20AC${deliveryCost.toFixed(2)}` : "Inclusa"}</span>
                </div>
              </div>

              <div className="border-t border-cream-dark pt-4 mb-6">
                <div className="flex justify-between font-semibold text-forest text-lg">
                  <span>Totale</span>
                  <span data-testid="cart-total">{"\u20AC"}{total.toFixed(2)}</span>
                </div>
              </div>

              <Button
                onClick={() => navigate("/checkout")}
                className="w-full bg-forest text-white hover:bg-forest-light py-6"
                data-testid="checkout-button"
              >
                Procedi al Checkout
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
